// Import Libraries
import React, { useState, useEffect, useRef, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilePdf, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

// Import Components
import ExportProjectButton from './Project/exportProjectButton';
import CollaboratorDropdown from './Project/collaboratorDropdown.js';
import CollaboratorBubbleList from './Project/collaboratorBubbleList.js';
import ScriptVersionSelect from './Project/ScriptVersionSelect.js';
import Icon from '../Elements/Icon';

// Context
import ScriptContext from '../../context/Script';
import SessionContext from '../../context/Session';
import ProjectViewContext from '../../context/ProjectView';

// Import Services
import utilities from '../../services/utilities';
import UserService from '../../services/userService';
import ScriptService from '../../services/scriptService';

// Construct Component
const ShotTableHeader = () => {
  const { script, setScript } = useContext(ScriptContext);
  const { isMobile } = useContext(SessionContext);
  const { view, setView, activeCollaborators } = useContext(ProjectViewContext);
  const [showCollabDropdown, setShowCollabDropdown] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    if (script) setNameValue(script.script_name || '');
  }, [script?.script_name]);

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  const canEditName = script?.level === 'owner' || script?.level === 'editor';

  const handleNameSave = async () => {
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === script.script_name) {
      setNameValue(script.script_name || '');
      setIsEditingName(false);
      return;
    }
    setNameSaving(true);
    try {
      await ScriptService.updateScriptName(script.id, trimmed);
      setScript({ ...script, script_name: trimmed });
      toast.success('Project title has been saved', { autoClose: 1000, hideProgressBar: true });
    } catch (e) {
      setNameValue(script.script_name || '');
      toast.error('Failed to save change. Please try again.');
    }
    setNameSaving(false);
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') handleNameSave();
    if (e.key === 'Escape') {
      setNameValue(script.script_name || '');
      setIsEditingName(false);
    }
  };

  function changePdfView() {
    setView({ ...view, pdfActive: !view.pdfActive });
  }

  const updateScript = async (updatedScript) => {
    setScript({ ...script, ...updatedScript });
  };

  useEffect(() => {
    if (isMobile) {
      setView({ ...view, viewColumns: 1 });
    }
  }, [isMobile]);

  return (
    <div className="fullWidthContainer shotHeaderBar">
      <span className={`${isMobile ? 'TenPortionFlex' : 'FivePortionFlex'} MinSmall`}>
        <div className="fullWidthContainer">
          {isMobile && view.onScreen === 'shot' ? (
            <span className="shotHeader" onClick={() => setView({ ...view, onScreen: 'scene' })}>
              <FontAwesomeIcon
                icon={faChevronLeft}
                className="linkText"
                style={{ paddingRight: '15px', paddingLeft: '0px' }}
              />{' '}
              BACK TO SCENES
            </span>
          ) : isEditingName ? (
            <input
              ref={nameInputRef}
              className="shotHeader ThreePortionFlex MinSmall shotHeaderNameInput"
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={handleNameSave}
              onKeyDown={handleNameKeyDown}
              disabled={nameSaving}
              maxLength={200}
            />
          ) : (
            <h1
              className={`shotHeader ThreePortionFlex MinSmall${canEditName ? ' shotHeaderNameEditable' : ''}`}
              onClick={() => canEditName && setIsEditingName(true)}
              title={canEditName ? 'Click to rename project' : undefined}
            >
              {script ? script.script_name : ''}
            </h1>
          )}

          <ScriptVersionSelect />

          <span style={{ flex: '0 0 auto' }}>
            <div
              className="Padded"
              style={{
                paddingLeft: '20px',
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'nowrap',
                gap: '0px',
              }}
            >
              <div style={{ flex: '0 0 auto', marginRight: '-5px' }}>
                <CollaboratorBubbleList
                  collaborators={script.collaborators}
                  setShowCollabDropdown={setShowCollabDropdown}
                  level={''}
                  activeList={activeCollaborators}
                />
              </div>
              {script.level && script.level == 'owner' && (
                <>
                  <button
                    className="buttonGray"
                    style={{ flexShrink: 0 }}
                    onClick={() => setShowCollabDropdown(!showCollabDropdown)}
                  >
                    Collaborate
                  </button>

                  <div className="scriptListCollabDropdownWrap">
                    {showCollabDropdown && (
                      <CollaboratorDropdown
                        script={script}
                        updateScript={updateScript}
                        setShow={setShowCollabDropdown}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </span>
        </div>
      </span>
      <div className={`ThreePortionFlex MinSmall ${view.isLoading ? 'hidden' : ''}`}>
        {script && script.page_count !== script.last_processed_page ? (
          <span className="progress-bar-text">
            <span>Still Processing:</span>
            <span className="progress-bar-container">
              <span
                className="progress-bar"
                style={{
                  width: `${
                    script && script.page_count !== script.last_processed_page
                      ? ((script.last_processed_page / script.page_count) * 100).toFixed(2)
                      : 0
                  }%`,
                }}
              ></span>
            </span>
          </span>
        ) : (
          <>
            <div
              className="FullWidthFlex"
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isMobile ? (
                <> </>
              ) : (
                <span className="ThreePortionFlex">
                  <span
                    className={`pageIcon ${view.pdfActive == true ? 'pageIconSelected' : ''}`}
                    onClick={() => changePdfView()}
                    style={{ display: 'none' }}
                  >
                    <FontAwesomeIcon icon={faFilePdf} style={{ padding: '5px 5px 5px 4px' }} />
                  </span>

                  <span
                    className={`pageIcon ${view.viewColumns == 1 && !view.tableView ? 'pageIconSelected' : ''}`}
                    onClick={() => setView({ ...view, viewColumns: 1, tableView: false })}
                  >
                    <Icon icon="List" size={16} color="Black" />
                    List
                  </span>
                  <span
                    className={`pageIcon ${view.viewColumns == 2 && !view.tableView ? 'pageIconSelected' : ''}`}
                    onClick={() => setView({ ...view, viewColumns: 2, tableView: false })}
                  >
                    <Icon icon="Card" size={16} color="Black" />
                    Cards
                  </span>
                  <span
                    className={`pageIcon ${view.tableView ? 'pageIconSelected' : ''}`}
                    style={{ position: 'relative' }}
                    onClick={() => {
                      UserService.addActionLog('Clicked Table View');
                      setView({ ...view, tableView: !view.tableView });
                    }}
                  >
                    <Icon icon="List" size={16} color="Black" />
                    Table
                    <span
                      style={{
                        position: 'absolute',
                        top: 1,
                        right: -6,
                        backgroundColor: '#7A64EB',
                        color: '#fff',
                        fontSize: 9,
                        fontWeight: 700,
                        borderRadius: 4,
                        padding: '1px 4px',
                        letterSpacing: '0.06em',
                        lineHeight: 1.4,
                        pointerEvents: 'none',
                      }}
                    >
                      BETA
                    </span>
                  </span>
                </span>
              )}
              <span className="TwoPortionFlex" style={{ marginRight: '15px' }}>
                <ExportProjectButton />
              </span>
            </div>
            <div
              className="FullWidthFlex"
              style={{
                position: 'relative',
                display: 'block',
              }}
            ></div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShotTableHeader;
